import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface EmailConfirmationBannerState {
  isVisible: boolean;
  dismissBanner: () => void;
  resendConfirmation: () => Promise<void>;
  isResending: boolean;
}

export const useEmailConfirmationBanner = (): EmailConfirmationBannerState => {
  const { user, session } = useAuth();
  const { toast } = useToast();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    // Debug: log user and session state
    console.log('🔍 [EmailBanner] Debug state:', {
      hasUser: !!user,
      hasSession: !!session,
      userEmailConfirmedAt: user?.email_confirmed_at,
      isDismissed,
      userEmail: user?.email
    });

    // Only show if user exists, is logged in, email is not confirmed, and not dismissed
    const shouldShow = user && 
                      session && 
                      !user.email_confirmed_at && 
                      !isDismissed;
    
    console.log('🔍 [EmailBanner] Should show:', shouldShow);
    setIsVisible(!!shouldShow);
  }, [user, session, isDismissed]);

  const dismissBanner = () => {
    setIsDismissed(true);
    setIsVisible(false);
  };

  const resendConfirmation = async () => {
    if (!user?.email) return;
    
    setIsResending(true);
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
        options: {
          emailRedirectTo: 'https://get-clan.com/dashboard'
        }
      });

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de renvoyer l'email de confirmation",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Email envoyé",
          description: "Un nouvel email de confirmation a été envoyé",
          variant: "success",
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return {
    isVisible,
    dismissBanner,
    resendConfirmation,
    isResending,
  };
};